const SELECTOR = "[data-counting-element]";
const DURATION = 1800;
const VISIBILITY_THRESHOLD = 0.2;

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

const clampPercentage = (value) => {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.min(100, Math.max(0, parsed));
};

const parseNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const getDecimals = (element) => {
    const parsed = parseInt(element.dataset.decimals || "0", 10);
    if (!Number.isFinite(parsed)) return 0;
    return Math.min(20, Math.max(0, parsed));
};

const createFormatter = (element) => {
    const decimals = getDecimals(element);
    const options = {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true,
    };
    const locale = document.documentElement && document.documentElement.lang
        ? document.documentElement.lang
        : undefined;
    try {
        return new Intl.NumberFormat(locale, options);
    } catch (e) {
        return new Intl.NumberFormat(undefined, options);
    }
};

const getTargets = (element) => ({
    valueElement: element.querySelector("[data-counting-value]"),
    progressElement: element.querySelector(".counting-element__progress"),
    formatter: createFormatter(element),
});

const render = (targets, numberValue, percentageValue) => {
    if (targets.valueElement) {
        targets.valueElement.textContent = targets.formatter.format(
            Number.isFinite(numberValue) ? numberValue : 0
        );
    }
    if (targets.progressElement) {
        const percentage = clampPercentage(percentageValue);
        const radius = parseFloat(targets.progressElement.getAttribute("r")) || 0;
        const circumference = 2 * Math.PI * radius;
        targets.progressElement.setAttribute("visibility", percentage > 0 ? "visible" : "hidden");
        targets.progressElement.setAttribute(
            "stroke-dashoffset",
            String(circumference * (1 - percentage / 100))
        );
    }
};

const animate = (element) => {
    const targets = getTargets(element);
    const start = parseNumber(element.dataset.startValue);
    const end = parseNumber(element.dataset.endValue);
    const targetPercentage = clampPercentage(element.dataset.circlePercentage);
    let startTime = null;

    const step = (now) => {
        if (startTime === null) startTime = now;
        const progress = Math.min(1, Math.max(0, (now - startTime) / DURATION));
        const eased = easeOut(progress);
        render(targets, start + (end - start) * eased, targetPercentage * eased);
        if (progress < 1) {
            requestAnimationFrame(step);
        } else {
            render(targets, end, targetPercentage);
        }
    };

    requestAnimationFrame(step);
};

const initCountingElements = () => {
    const elements = document.querySelectorAll(SELECTOR);
    if (!elements.length) return;

    if (
        typeof IntersectionObserver === "undefined" ||
        typeof requestAnimationFrame === "undefined"
    ) {
        return;
    }

    elements.forEach((element) => {
        render(getTargets(element), parseNumber(element.dataset.startValue), 0);
    });

    const animated = new WeakSet();
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (
                    entry.isIntersecting &&
                    entry.intersectionRatio >= VISIBILITY_THRESHOLD &&
                    !animated.has(entry.target)
                ) {
                    animated.add(entry.target);
                    observer.unobserve(entry.target);
                    animate(entry.target);
                }
            });
        },
        { threshold: VISIBILITY_THRESHOLD }
    );

    elements.forEach((element) => observer.observe(element));
};

const init = () => {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initCountingElements);
    } else {
        initCountingElements();
    }
};

init();
